/**
 * Source of truth: MTU `CF34-8E_GenericInput_V7.xlsx`, revision V7 dated
 * 2022-11-11 (SHA-256: a914cc4d72b1c48b6ae4e3b2c118399cace3db480eb9bac3ecef17079399e7e4).
 * The workbook's `Revision` sheet is older than its content: threaded
 * comments on the COMBUSTION CHAMBER ASSY 4180T27G03 row and on the 8E5A2
 * column of the LPT rows say "ESM 05-11-04 update 06/26/2026".
 *
 * IINs come from `LLP Configs`; part numbers, manufacture ranges, thrust
 * variants, and cycle limits come from `LLP_Table_FC`. MTU lists component
 * names as `NOUN, QUALIFIER` (e.g. `DISK, STG 1 HPT`); the names below are
 * the same words in natural order, and each component carries MTU's
 * verbatim name in a comment. Module groups follow the IIN prefix
 * (2xx fan, 3xx HPC, 4xx combustor, 5xx HPT, 6xx LPT).
 *
 * Values are transcribed as displayed, including the `N/L` that the three
 * 41,100-cycle COMBUSTION CHAMBER ASSY part numbers show on 8E5A2HA only —
 * that reading is under query with MTU (`N/A` is the more plausible intent).
 * Two rows are affected by ADs the catalog shape cannot express; each is
 * called out inline. DR-3228 tracks SB/AD-aware limit resolution.
 */
const VARIANTS = ['8E2', '8E2A1', '8E5', '8E5A1', '8E5A2', '8E5A2HA', '8E6', '8E6A1'];
const MANUFACTURE_RANGE = {
    start: '1999-01-01',
    end: '2021-01-01',
};
const NL = 'N/L';
function limitsByValue(partNumber, limits) {
    if (limits.length !== VARIANTS.length) {
        throw new Error(`CF34-8E ${partNumber} has ${limits.length} limits for ${VARIANTS.length} variants`);
    }
    return Object.fromEntries([...new Set(limits)].map((limit) => [String(limit), VARIANTS.filter((_, index) => limits[index] === limit)]));
}
function component(componentName, iin, parts) {
    return {
        componentName,
        iin,
        partNumbers: parts.map(([partNumber]) => partNumber),
        partLimits: Object.fromEntries(parts.map(([partNumber, limits]) => [partNumber, limitsByValue(partNumber, limits)])),
        partManufacture: Object.fromEntries(parts.map(([partNumber]) => [partNumber, MANUFACTURE_RANGE])),
    };
}
export const cf34_8e = {
    engineType: 'CF34-8E',
    groups: [
        {
            groupName: 'Fan Module',
            components: [
                // MTU: DISK, FAN
                component('Fan Disk', '212', [
                    ['4114T16G02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SHAFT, FAN DRIVE
                component('Fan Drive Shaft', '220', [
                    ['4118T61P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
            ],
        },
        {
            groupName: 'HPC Module',
            components: [
                // MTU: CASE, COMPRESSOR
                component('Compressor Case', '311', [
                    ['1822M28G01', [72000, 72000, 79000, 74000, 65000, 65000, 72000, 65000]],
                    ['1822M28G04', [NL, NL, NL, NL, NL, NL, NL, NL]],
                    ['4923T98G01', [72000, 72000, 79000, 74000, 65000, 65000, 72000, 65000]],
                    ['4923T98G02', [72000, 72000, 79000, 74000, 65000, 65000, 72000, 65000]],
                ]),
                // MTU: SHAFT, HP COMPRESSOR FWD
                component('HP Compressor Forward Shaft', '321', [
                    ['4128T91G04', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4128T91G05', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4128T91G06', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4128T91G07', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: BLISK, COMPR ROTOR STG 1-2
                component('Compressor Rotor Stage 1-2 Blisk', '322', [
                    ['4129T60P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4129T60P02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: BLISK, COMPR ROTOR STG 3
                component('Compressor Rotor Stage 3 Blisk', '323', [
                    ['4129T59P02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SPOOL, COMPR ROTOR AFT SHAFT
                component('Compressor Rotor Aft Shaft Spool', '324', [
                    ['4129T50G02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4129T50G03', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4129T50G04', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SEAL, COMPR DISCHARGE (CDP SEAL)
                component('Compressor Discharge Pressure Seal', '328', [
                    ['4129T28P05', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SPOILER, HP COMPR ROTOR VORTEX
                component('HP Compressor Rotor Vortex Spoiler', '331', [
                    ['4129T40P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
            ],
        },
        {
            groupName: 'Combustor Module',
            components: [
                // MTU: COMBUSTION CHAMBER ASSY
                component('Combustion Chamber Assembly', '402', [
                    ['4145T11G08', [41100, 41100, 41100, 41100, 41100, NL, 41100, 41100]],
                    ['4145T11G09', [41100, 41100, 41100, 41100, 41100, NL, 41100, 41100]],
                    ['4180T27G01', [41100, 41100, 41100, 41100, 41100, NL, 41100, 41100]],
                    ['4180T27G03', [25000, 25000, 25000, 23000, 23000, 23000, 25000, 23000]],
                ]),
            ],
        },
        {
            groupName: 'HPT Module',
            components: [
                // MTU: COOLING PLATE, STG 1 HPT FWD
                component('HPT Stage 1 Forward Cooling Plate', '523', [
                    ['4125T30P01', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                    ['4125T30P02', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                ]),
                // MTU: DISK, STG 1 HPT
                component('HPT Stage 1 Disk', '524', [
                    ['4125T22P03', [24200, 24200, 24200, 20000, 20000, 20000, 24200, 20000]],
                    // AD 2021-26-19 removes listed serials of this disk; the catalog cannot express that.
                    ['4125T22P04', [24200, 24200, 24200, 20000, 20000, 20000, 24200, 20000]],
                ]),
                // MTU: COUPLING, HPT OUTER TORQUE
                component('HPT Outer Torque Coupling', '528', [
                    ['4125T37P01', [10300, 10300, 10300, 3000, 3000, 3000, 10300, 3000]],
                    ['4125T37P02', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                ]),
                // MTU: DISK, STG 2 HPT
                component('HPT Stage 2 Disk', '530', [
                    ['4125T23P03', [11500, 11500, 11500, 3000, 3000, 3000, 11500, 3000]],
                    ['4125T23P04', [23500, 23500, 24100, 20000, 20000, 20000, 23500, 20000]],
                ]),
                // MTU: COOLING PLATE, STG 2 HPT AFT
                component('HPT Stage 2 Aft Cooling Plate', '532', [
                    ['4124T95P03', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                ]),
                // MTU: AIR, SEAL IBP ROTATING
                component('HPT Inner Balance Piston Rotating Air Seal', '533', [
                    ['2651M27P01', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                    ['4124T93P02', [15400, 15400, 15400, 3000, 3000, 3000, 15400, 3000]],
                    ['4124T93P03', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                    ['4124T93P04', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                ]),
                // MTU: AIR, SEAL HPT OBP ROTATING
                component('HPT Outer Balance Piston Rotating Air Seal', '534', [
                    ['4124T92P01', [15700, 15700, 15700, 6000, 6000, 6000, 15700, 6000]],
                    ['4124T92P02', [25000, 25000, 25000, 20000, 20000, 20000, 25000, 20000]],
                ]),
            ],
        },
        {
            groupName: 'LPT Module',
            components: [
                // MTU: SHAFT, REAR LPT
                component('LPT Rear Shaft', '631', [
                    ['4118T62P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: DISK, STG 3 LPT
                component('LPT Stage 3 Disk', '632', [
                    ['4118T63P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4118T63P02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4908T10P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SEAL, STG 3 AND 4 LPT
                component('LPT Stage 3-4 Seal', '634', [
                    ['4117T16P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: DISK, STG 4 LPT
                component('LPT Stage 4 Disk', '635', [
                    ['4117T13P04', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4117T13P05', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SEAL, STG 4 AND 5 LPT
                component('LPT Stage 4-5 Seal', '637', [
                    ['4117T17P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: DISK, STG 5 LPT
                component('LPT Stage 5 Disk', '639', [
                    // AD 2022-21-06 removes this disk at 8,000 CSN; the catalog keeps MTU's baseline.
                    ['4117T14P02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                    ['4117T14P03', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: SEAL, STG 5 AND 6 LPT
                component('LPT Stage 5-6 Seal', '641', [
                    ['4117T18P01', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
                // MTU: DISK, STG 6 LPT
                component('LPT Stage 6 Disk', '642', [
                    ['4117T15P02', [25000, 25000, 25000, 25000, 25000, 25000, 25000, 25000]],
                ]),
            ],
        },
    ],
};
//# sourceMappingURL=cf34-8e.js.map